import React, { type RefObject, type UIEventHandler, useEffect, Children, isValidElement } from 'react';
import classNames from 'classnames';
import { noop } from 'lodash';
import { assignInlineVars } from '@fiverr-private/theme';
import { Container, Stack } from '@fiverr-private/layout_components';
import { useCarouselContext } from '../Carousel/constants';

import {
  leftFadeStyles,
  rightFadeStyles,
  cardsFadeStyles,
  swipeModeStyles,
  fadeBackgroundColor,
} from '../styles.ve.css';
import { TrackProps } from './types';
import { fadeColors, TRACK_ID, TrackContextProvider } from './constants';

const Track = ({
  slidesGap = '4',
  borderRadius = 'none',
  autoSlidesWidth = false,
  showCardsFade = false,
  cardsFadeColor = 'light',
  children,
}: TrackProps) => {
  const {
    slidesTrackRef,
    onTransitionEnd,
    swipeMode,
    supportSlidesIndicator,
    updateCounterPositionOnScrollBySwipe = noop,
    leftSlideFullyVisible,
    rightSlideFullyVisible,
  } = useCarouselContext();

  useEffect(() => {
    const { current } = slidesTrackRef as RefObject<HTMLDivElement>;

    if (!current) {
      return;
    }
    current.addEventListener('scrollend', onTransitionEnd as EventListener);

    return () => current.removeEventListener('scrollend', onTransitionEnd as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slidesTrackRef]);

  const onSwipe: UIEventHandler = (event) => {
    if (swipeMode && supportSlidesIndicator) {
      updateCounterPositionOnScrollBySwipe(event);
    }
  };

  const supportsCardsFade = showCardsFade && !swipeMode;
  const showSwipeHalfCard = swipeMode && !supportSlidesIndicator;

  const fadeColor = fadeColors[cardsFadeColor];

  return (
    <TrackContextProvider value={{ slidesGap, autoSlidesWidth, showSwipeHalfCard }}>
      <Container
        position="relative"
        overflow="hidden"
        borderRadius={borderRadius}
        className={classNames({
          [cardsFadeStyles]: supportsCardsFade,
          [rightFadeStyles]: supportsCardsFade && !rightSlideFullyVisible,
          [leftFadeStyles]: supportsCardsFade && !leftSlideFullyVisible,
        })}
        style={assignInlineVars({
          [fadeBackgroundColor]: fadeColor,
        })}
      >
        <Stack
          ref={slidesTrackRef}
          columnGap={slidesGap}
          paddingX={showSwipeHalfCard ? '6' : '0'}
          overflowX={swipeMode ? 'auto' : undefined}
          onTransitionEnd={onTransitionEnd}
          onScroll={onSwipe}
          className={classNames({
            [swipeModeStyles]: swipeMode,
          })}
        >
          {Children.toArray(children).filter((item) => isValidElement(item))}
        </Stack>
      </Container>
    </TrackContextProvider>
  );
};

Track.id = TRACK_ID;
Track.displayName = TRACK_ID;
export default Track;
